import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Card, PageHeader, Spinner } from '@/components/ui';
import { FleetDashboardSummary, FleetFilterOptions } from '../../../types/Vehicle';
import equipmentFleetService from '../../../services/equipmentFleetService';
import EquipmentSubnav from '../components/EquipmentSubnav';
import { EquipmentScopeFilters, useEquipmentScope } from '../components/EquipmentFilters';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';

/**
 * Equipment Dashboard (EQ-16): one tile per Fleet list filter. Each tile opens the Fleet list with
 * that filter and the Dashboard's Service Area / Foreman Area, and the server counts the tile with the
 * list's own query, so the numbers match.
 */
const EquipmentDashboardPage: React.FC = () => {
  useDocumentTitle('Equipment');
  const [options, setOptions] = useState<FleetFilterOptions | null>(null);
  const { scope, setScope, defaultScope } = useEquipmentScope(options);
  const [summary, setSummary] = useState<FleetDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    equipmentFleetService
      .getFilterOptions()
      .then(setOptions)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load the filters');
        setOptions({ currentStatuses: [], serviceAreas: [], vehicleClassifications: [], currentYards: [], vehicleTypes: [], foremanAreas: [], myForemanAreaIds: [] });
      });
  }, []);

  const scopeKey = scope ? `${scope.serviceArea}|${scope.foremanArea}` : null;
  useEffect(() => {
    if (!scope) return;
    let cancelled = false;
    setLoading(true);
    equipmentFleetService
      .getDashboardSummary(scope)
      .then((data) => {
        if (cancelled) return;
        setSummary(data);
        setError(null);
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Failed to load the Dashboard'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  return (
    <div className="tw-w-full tw-p-4">
      <PageHeader title="Equipment" />
      <EquipmentSubnav />

      <p className="tw-mb-3 tw-text-sm tw-text-content-secondary">
        Fleet status for your service areas. Select a tile to see the units.
      </p>

      {options && scope && (
        <EquipmentScopeFilters options={options} scope={scope} defaultScope={defaultScope} onChange={setScope} />
      )}

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && (
        <div className="tw-py-8 tw-text-center">
          <Spinner role="status"><span className="tw-sr-only">Loading...</span></Spinner>
        </div>
      )}

      {!loading && summary?.groups.map((group) => (
        <section key={group.id} className="tw-mb-6">
          <h2 className="tw-mb-3 tw-text-lg tw-font-semibold tw-text-content">{group.label}</h2>
          <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
            {group.tiles.map((tile) => (
              <Link key={tile.key} to={tile.href} className="tw-no-underline" data-testid={`tile-${tile.key}`}>
                <Card className="tw-h-full tw-transition hover:tw-shadow-md">
                  <Card.Body>
                    <div className="tw-text-3xl tw-font-semibold tw-tabular-nums tw-text-content">
                      {tile.count.toLocaleString('en-CA')}
                    </div>
                    <div className="tw-mt-1 tw-text-sm tw-text-content-secondary">{tile.label}</div>
                  </Card.Body>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default EquipmentDashboardPage;
